import { FC, ReactNode, useEffect } from "react";
import { ConvexProvider } from "convex/react";
import { convexClient } from "./convex-client";
import { createConvexAuthToken } from "./backend-api";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { observer } from "mobx-react-lite";

// Smart Views auth is set up here, but rendering of the app must NEVER block
// on it. If the Convex deployment can't authenticate (auth config not yet
// deployed, backend not reachable, etc.), Smart Views queries will fail —
// the rest of the app should still work.
export const SmartViewsConvexProvider: FC<{ children: ReactNode }> = observer(({ children }) => {
  const { store } = useAppStore();
  const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccount?.id;

  useEffect(() => {
    if (!spaceAccountId) {
      convexClient.clearAuth();
      return;
    }

    convexClient.setAuth(
      async () => {
        try {
          const response = await createConvexAuthToken({ spaceAccountId });
          return response.token;
        } catch (err) {
          logger.error({
            message: "Failed to create Smart Views Convex auth token",
            info: { err: String(err) },
          });
          return null;
        }
      },
      () => {
        /* Auth state is observed by Convex; we don't gate rendering on it. */
      }
    );

    return () => {
      convexClient.clearAuth();
    };
  }, [spaceAccountId]);

  return <ConvexProvider client={convexClient}>{children}</ConvexProvider>;
});
